import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import type { UserProfile } from '../types';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      if (!firebaseUser) {
        setUserProfile(null);
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  // Listen to user profile document when authenticated
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          setUserProfile({ uid: snap.id, ...snap.data() } as UserProfile);
        } else {
          // No profile doc yet — create it client-side
          const defaultProfile: Omit<UserProfile, 'uid'> = {
            email: user.email ?? '',
            role: 'user',
            subscriptionTier: 'free',
            stripeCustomerId: null,
            stripeSubscriptionId: null,
            subscriptionStatus: 'none',
            createdAt: Date.now(),
          };
          setDoc(doc(db, 'users', user.uid), defaultProfile).catch(console.error);
          setUserProfile({ uid: user.uid, ...defaultProfile });
        }
        setLoading(false);
      },
      () => {
        // Firestore error (e.g. permissions) — still stop loading
        setLoading(false);
      }
    );
    return unsub;
  }, [user]);

  const isAdmin = userProfile?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}
